import { Check } from 'lucide-react';
import type { MarkWords, RailSection } from './types';

/**
 * The section list (crossmarc/steward-app#1886, lightened by #2159), rendered once in two shells
 * by `FormShell`: a persistent rail from `lg` up, and the sheet the phone's "Section N of M"
 * button opens. The jump behaviour, the `aria-current` handling and the accessible names live
 * here so the two cannot drift apart.
 *
 * A row is a plain section name with no number. What it shows at its right edge:
 *  - done: one small bronze check;
 *  - started: a small bronze ring, ONLY when `showStarted` is set (the onboarding form, where
 *    "started" means "something is still missing" and the family needs to see which section);
 *    without it a started row shows nothing, as the intake's "viewed" is not worth a mark;
 *  - not started: nothing.
 *
 * Current is more than a colour: a 2px bronze bar at the row's edge, a medium-weight label in
 * `bronze-text` (the text-role shade; plain bronze is ~3:1 on the page), and `aria-current`.
 * A row's state is said aloud through `aria-label`, because the glyph is hidden from assistive
 * technology: every row not current is named with its state, and the current row is too when
 * it shows a mark (`aria-current` carries "current", the label carries "done"). A current row
 * with no visible mark says nothing extra.
 *
 * The Review row, when there is one, sits under a hairline.
 */
const ROW =
  'flex w-full items-center gap-3 border-l-2 bg-transparent py-2.5 pl-3 pr-2 text-left ' +
  'font-sans text-[13.5px] leading-snug transition-colors duration-150 ' +
  'disabled:cursor-wait disabled:opacity-50 max-sm:min-h-11 lg:py-2';

function RailMark({ mark, showStarted }: { mark: RailSection['mark']; showStarted: boolean }) {
  if (mark === 'done') {
    return (
      <span aria-hidden="true" data-mark="done" className="ml-auto inline-flex shrink-0 text-bronze">
        <Check size={14} strokeWidth={2.5} />
      </span>
    );
  }
  if (mark === 'started' && showStarted) {
    return (
      <span aria-hidden="true" data-mark="started" className="ml-auto inline-block h-2.5 w-2.5 shrink-0 rounded-full border-2 border-bronze" />
    );
  }
  return null;
}

export function SectionRail({ sections, currentKey, words, onSelect, disabled = false, showStarted = false }: {
  sections: readonly RailSection[];
  currentKey: string;
  words: MarkWords;
  onSelect: (key: string) => void;
  disabled?: boolean;
  showStarted?: boolean;
}) {
  return (
    <ol className="m-0 list-none p-0">
      {sections.map((section, i) => {
        const isCurrent = section.key === currentKey;
        const marked = section.mark === 'done' || (section.mark === 'started' && showStarted);
        const tone = isCurrent
          ? 'border-bronze font-medium text-bronze-text'
          : 'border-transparent text-navy hover:border-line-strong';
        return (
          <li key={section.key} className={section.review && i > 0 ? 'mt-2 border-t border-line pt-2' : undefined}>
            <button
              type="button"
              aria-current={isCurrent ? 'step' : undefined}
              aria-label={isCurrent && !marked ? undefined : `${section.label}, ${words[section.mark]}`}
              onClick={() => onSelect(section.key)}
              disabled={disabled}
              className={`${ROW} ${tone}`}
            >
              <span className="min-w-0">{section.label}</span>
              <RailMark mark={section.mark} showStarted={showStarted} />
            </button>
          </li>
        );
      })}
    </ol>
  );
}
